<script lang="ts">
  import { app } from "../../state/app.svelte";
  import { i18n } from "../../i18n/i18n.svelte";

  const fr = {
    close: "Fermer",
    title: "Comment utiliser Personal RAG",
    understood: "J'ai compris",
  };
  const en: typeof fr = {
    close: "Close",
    title: "How to use Personal RAG",
    understood: "Got it",
  };
  const t = $derived(i18n.locale === "en" ? en : fr);

  function onKeydown(e: KeyboardEvent) {
    if (app.tutorialOpen && e.key === "Escape") app.tutorialOpen = false;
  }
</script>

<svelte:window onkeydown={onKeydown} />

{#if app.tutorialOpen}
  <div
    class="backdrop"
    onclick={() => (app.tutorialOpen = false)}
    onkeydown={() => {}}
    role="button"
    tabindex="-1"
    aria-label={t.close}
  >
    <div
      class="modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tuto-title"
      onclick={(e) => e.stopPropagation()}
      onkeydown={() => {}}
      tabindex="-1"
    >
      <h2 id="tuto-title">{t.title}</h2>

      {#if i18n.locale === "en"}
      <section>
        <h3>1. Your documents</h3>
        <p>
          <strong>Docs</strong> tab: import your PDFs, Word files, etc. They are
          split and indexed <em>in your browser</em>. Then tick the documents to
          use — only <strong>active</strong> documents are consulted by the
          assistant. With no active document, it only works on the text you
          paste into the chat.
        </p>
      </section>

      <section>
        <h3>2. The toggles below the input box</h3>
        <ul class="bubbles">
          <li><span class="pill">+</span> attach an image or a document to the question.</li>
          <li>
            <span class="pill">FullText</span> sends the active documents
            <strong>in full</strong> instead of only the retrieved passages —
            useful for comparing or summarising a short document, costly in
            tokens on a large one.
          </li>
          <li>
            <span class="pill">EPFL</span> adds EPFL sources: the
            <strong>epfl.ch</strong> website, the ServiceNow knowledge base and
            resolved tickets. Requires signing in with your EPFL account (gear
            icon, top right).
          </li>
          <li>
            <span class="pill">Web</span> adds results from the public web (via
            Exa). Your queries then leave EPFL's infrastructure: avoid it for
            confidential content or content containing personal data.
          </li>
          <li>
            <span class="pill">Thinking</span> turns the assistant into an agent:
            it chooses its own searches among the enabled sources, runs them
            again if needed, can read a whole document, and thinks before
            answering — more thorough, sometimes slower. When off, a single
            search then the answer: fast, and enough for reading your
            documents.
          </li>
        </ul>
        <p class="tip">
          An enabled toggle is <strong>tinted teal</strong>. The toggles, not
          the assistant, decide which sources are available: if it replies that
          it has no access to a source, turn on the matching toggle and ask the
          question again.
        </p>
      </section>

      <section>
        <h3>3. Reading and exporting the answer</h3>
        <p>
          Every statement carries a <code>[N]</code> citation: click it to open
          the source passage with its page. Below the answer,
          <strong>Copy</strong> and <strong>Word</strong> retrieve the text.
          The <strong>History</strong> tab keeps your conversations.
        </p>
      </section>
      {:else}
      <section>
        <h3>1. Vos documents</h3>
        <p>
          Onglet <strong>Docs</strong> : importez vos PDF, Word, etc. Ils sont
          découpés et indexés <em>dans votre navigateur</em>. Cochez ensuite les
          documents à utiliser — seuls les documents <strong>actifs</strong> sont
          consultés par l'assistant. Sans document actif, il ne travaille que sur
          le texte que vous collez dans le chat.
        </p>
      </section>

      <section>
        <h3>2. Les bulles sous la zone de saisie</h3>
        <ul class="bubbles">
          <li><span class="pill">+</span> joindre une image ou un document à la question.</li>
          <li>
            <span class="pill">FullText</span> envoie les documents actifs
            <strong>en entier</strong> au lieu des seuls passages retrouvés — utile
            pour comparer ou résumer un document court, coûteux en tokens sur un
            gros document.
          </li>
          <li>
            <span class="pill">EPFL</span> ajoute les sources EPFL : site
            <strong>epfl.ch</strong>, base de connaissances ServiceNow et tickets
            résolus. Nécessite d'être connecté avec votre compte EPFL (engrenage
            en haut à droite).
          </li>
          <li>
            <span class="pill">Web</span> ajoute des résultats du web public (via
            Exa). Vos requêtes sortent alors de l'infrastructure EPFL : à éviter
            pour des contenus confidentiels ou contenant des données
            personnelles.
          </li>
          <li>
            <span class="pill">Réflexion</span> transforme l'assistant en agent :
            il choisit lui-même ses recherches parmi les sources activées, les
            relance si besoin, peut lire un document en entier, et réfléchit
            avant de répondre — plus approfondi, parfois plus lent. Éteinte,
            une seule recherche puis la réponse : rapide, et suffisant pour
            lire vos documents.
          </li>
        </ul>
        <p class="tip">
          Une bulle activée est <strong>teintée de bleu canard</strong>. C'est elle, et
          non l'assistant, qui décide des sources disponibles : s'il répond qu'il
          n'a pas accès à une source, activez la bulle correspondante et reposez
          la question.
        </p>
      </section>

      <section>
        <h3>3. Lire et exporter la réponse</h3>
        <p>
          Chaque affirmation porte une citation <code>[N]</code> : cliquez dessus
          pour ouvrir le passage source avec sa page. Sous la réponse,
          <strong>Copier</strong> et <strong>Word</strong> récupèrent le texte.
          L'onglet <strong>Historique</strong> conserve vos conversations.
        </p>
      </section>
      {/if}

      <div class="actions">
        <button class="primary" onclick={() => (app.tutorialOpen = false)}>
          {t.understood}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 110;
    padding: 1rem;
    animation: fade 0.12s ease-out;
  }

  .modal {
    background: var(--epfl-white);
    border-radius: var(--epfl-radius-lg);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.25);
    max-width: 460px;
    width: 100%;
    max-height: 85vh;
    overflow-y: auto;
    padding: 1.1rem 1.2rem 1rem;
    animation: slide 0.15s ease-out;
  }

  h2 {
    margin: 0 0 0.8rem;
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--epfl-text);
  }

  h3 {
    margin: 0 0 0.35rem;
    font-size: 0.82rem;
    font-weight: 600;
    color: var(--epfl-accent);
  }

  section {
    margin-bottom: 0.9rem;
  }

  p {
    margin: 0 0 0.5rem;
    font-size: 0.82rem;
    line-height: 1.45;
    color: var(--epfl-gray-700);
  }

  .bubbles {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  .bubbles li {
    font-size: 0.82rem;
    line-height: 1.45;
    color: var(--epfl-gray-700);
  }

  .pill {
    display: inline-block;
    margin-right: 0.3rem;
    padding: 0.1rem 0.45rem;
    border: 1px solid var(--epfl-accent);
    border-radius: 999px;
    background: var(--epfl-accent-bg);
    color: var(--epfl-accent);
    font-size: 0.74rem;
    font-weight: 500;
    white-space: nowrap;
  }

  .tip {
    margin-top: 0.55rem;
    padding: 0.45rem 0.6rem;
    background: var(--epfl-gray-50);
    border-left: 3px solid var(--epfl-gray-200);
    border-radius: var(--epfl-radius);
    margin-bottom: 0;
  }

  code {
    font-size: 0.78rem;
    background: var(--epfl-gray-100);
    padding: 0 0.2rem;
    border-radius: 3px;
  }

  strong {
    color: var(--epfl-text);
  }

  .actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 0.4rem;
  }

  button {
    padding: 0.45rem 0.9rem;
    border-radius: var(--epfl-radius);
    font-size: 0.82rem;
    font-family: inherit;
    cursor: pointer;
    border: 1px solid transparent;
  }

  .primary {
    background: var(--epfl-accent);
    color: var(--epfl-white);
  }

  .primary:hover {
    background: var(--epfl-accent-dark);
  }

  @keyframes fade {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes slide {
    from {
      transform: translateY(12px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
</style>
